import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import FeedbackReplyPopup from '../../../../../components/FeedbackReplyPopup'
import IPSelectBox from '../../../../../components/IPSelectBox'
import Table from '../../../../../components/Table'
import { DEFAULT_EXCLUDE_TEST_DEALERS, DealerTypeFilter } from '../analyticsQuery'
import { exportUsageToExcel } from '../dealerUsageExcel'
import { adminFeedbackReasonLabel } from '../feedbackReasons'
import {
  AnnouncedFilter,
  announcedFilterToQuery,
  RESOLUTION_STATUS_OPTIONS,
  reopenConfirmText,
  resolutionStatusLabel,
  resolveErrorMessage,
} from '../feedbackResolution'
import {
  FeedbackResolutionStatus,
  IChatResolutionReport,
  IChatResolutionRow,
  IResolutionReportParams,
} from '../models'
import { buildResolutionSheetRows } from '../reportExcel'
import { DEFAULT_LOAD_ERROR_TEXT, formatCount, formatLocalDateTime, loadErrorMessage } from '../viewModel'
import { UsagePagination } from './DealerUsageTab'
import QuestionAnswerCell from './QuestionAnswerCell'
import { RemoteBadge } from './RemoteBadge'
import styles from './ChatbotAnalytics.module.scss'

const PAGE_SIZE = 50
const EXPORT_PAGE_SIZE = 5000
const SEARCH_DEBOUNCE_MS = 400
const DASH = '—'

type LoadState = 'loading' | 'ready' | 'empty' | 'error'

export type ResolutionReportLoader = (params: IResolutionReportParams) => Promise<IChatResolutionReport>
export type ReopenResolutionAction = (resolutionId: number) => Promise<void>

export interface ResolutionsTabProps {
  /** Kapatma zamanına uygulanır. */
  fromUtc: Date
  toUtc: Date
  excludeTestDealers?: boolean
  /** "Bayi Tipi" (shell'den gelir; Excel dahil her isteğe eklenir). */
  dealerType?: DealerTypeFilter
  loadResolutions: ResolutionReportLoader
  reopenResolution: ReopenResolutionAction
  /** Yeniden açma başarılı: shell özet sayılarını yeniler. */
  onReopened?: () => void
}

const statusOptions: { value: '' | FeedbackResolutionStatus; label: string }[] = [
  { value: '', label: 'Tüm statüler' },
  ...RESOLUTION_STATUS_OPTIONS,
]

const announcedOptions: { value: AnnouncedFilter; label: string }[] = [
  { value: '', label: 'Duyuru: Tümü' },
  { value: 'yes', label: 'Duyuruldu' },
  { value: 'no', label: 'Duyurulmadı' },
]

/**
 * Tamamlananlar (Faz-37 §2, ADR-022): sonuçlandırılmış olumsuz geri bildirimler, BAYİ düzeyinde.
 * Popup'ı gören kullanıcılar yalnız sayı + ilk görülme olarak gösterilir; kullanıcı adı yoktur.
 */
export default function ResolutionsTab({
  fromUtc,
  toUtc,
  excludeTestDealers = DEFAULT_EXCLUDE_TEST_DEALERS,
  dealerType,
  loadResolutions,
  reopenResolution,
  onReopened,
}: ResolutionsTabProps) {
  const [status, setStatus] = useState<FeedbackResolutionStatus | undefined>(undefined)
  const [announced, setAnnounced] = useState<AnnouncedFilter>('')
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  // Sunucunun kullanıcıya dönük hata metni (ör. 90 gün sınırı) panelde gösterilir.
  const [errorMessage, setErrorMessage] = useState(DEFAULT_LOAD_ERROR_TEXT)
  const [report, setReport] = useState<IChatResolutionReport | null>(null)
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})
  const [exporting, setExporting] = useState(false)
  const [previewRow, setPreviewRow] = useState<IChatResolutionRow | null>(null)
  const [reopeningId, setReopeningId] = useState<number | null>(null)

  const requestSequenceRef = useRef(0)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      requestSequenceRef.current += 1
    }
  }, [])

  // Arama kutusu her tuşta istek atmaz; terim değişince sayfa 1'e döner (DealerUsageTab deseni).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(current => (current === searchDraft ? current : searchDraft))
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchDraft])

  const currentParams = useCallback(
    (overrides?: Partial<IResolutionReportParams>): IResolutionReportParams => ({
      fromUtc,
      toUtc,
      status,
      announced: announcedFilterToQuery(announced),
      search: search.trim() || undefined,
      page,
      pageSize: PAGE_SIZE,
      export: false,
      excludeTestDealers,
      dealerType,
      ...overrides,
    }),
    [fromUtc, toUtc, status, announced, search, page, excludeTestDealers, dealerType]
  )

  const load = useCallback(async () => {
    const sequence = ++requestSequenceRef.current
    setLoadState('loading')

    try {
      const response = await loadResolutions(currentParams())
      // Geç dönen yanıt yeniyi EZMEZ.
      if (!mountedRef.current || sequence !== requestSequenceRef.current) return
      setReport(response)
      setExpandedRows({})
      setLoadState(response.Page.TotalRows === 0 ? 'empty' : 'ready')
    } catch (error) {
      if (!mountedRef.current || sequence !== requestSequenceRef.current) return
      setErrorMessage(loadErrorMessage(error))
      setLoadState('error')
    }
  }, [currentParams, loadResolutions])

  useEffect(() => {
    load()
  }, [load])

  const toggleRow = (index: number) => setExpandedRows(current => ({ ...current, [index]: !current[index] }))

  const exportToExcel = async () => {
    setExporting(true)
    try {
      // Sayfalanmış görünüm değil, aynı filtrelerin TAM sonuç kümesi (en fazla 5000) indirilir.
      const response = await loadResolutions(currentParams({ page: 1, pageSize: EXPORT_PAGE_SIZE, export: true }))
      exportUsageToExcel(
        buildResolutionSheetRows(response.Page.Rows),
        'chatbot-tamamlananlar.xlsx',
        'Tamamlananlar'
      )
    } catch (error) {
      if (mountedRef.current) {
        setErrorMessage(loadErrorMessage(error))
        setLoadState('error')
      }
    } finally {
      if (mountedRef.current) setExporting(false)
    }
  }

  const reopen = async (row: IChatResolutionRow) => {
    if (reopeningId !== null) return
    const answer = await Swal.fire({
      icon: 'warning',
      title: 'Yeniden aç',
      text: reopenConfirmText(row.Announced),
      showCancelButton: true,
      confirmButtonText: 'Evet, yeniden aç',
      cancelButtonText: 'Vazgeç',
    })
    if (!answer.isConfirmed) return

    setReopeningId(row.ResolutionId)
    try {
      await reopenResolution(row.ResolutionId)
      if (!mountedRef.current) return
      load()
      onReopened?.()
    } catch (error) {
      if (!mountedRef.current) return
      Swal.fire({ icon: 'error', title: 'Yeniden açılamadı', text: resolveErrorMessage(error) })
      load()
    } finally {
      if (mountedRef.current) setReopeningId(null)
    }
  }

  const columns = useMemo(
    () => [
      { Header: 'Kapatma tarihi', accessor: 'closedAt', disableSortBy: true },
      { Header: 'Bayi', accessor: 'dealer', disableSortBy: true },
      { Header: 'Neden', accessor: 'reason', disableSortBy: true },
      { Header: 'Yorum', accessor: 'comment', disableSortBy: true },
      { Header: 'Soru', accessor: 'question', disableSortBy: true },
      { Header: 'Statü', accessor: 'status', disableSortBy: true },
      { Header: 'İç not', accessor: 'note', disableSortBy: true },
      { Header: 'Popup', accessor: 'popup', disableSortBy: true },
      { Header: 'Duyuruldu', accessor: 'announced', disableSortBy: true },
      { Header: 'Kapatan', accessor: 'closedBy', disableSortBy: true },
      { Header: 'Görüntüleme', accessor: 'views', disableSortBy: true },
      { Header: 'İşlem', accessor: 'action', disableSortBy: true },
    ],
    []
  )

  const rows = useMemo(
    () =>
      (report?.Page.Rows ?? []).map((row, index) => {
        const expanded = !!expandedRows[index]
        const clampClass = expanded ? styles.cellFull : styles.cellClamp

        return {
          closedAt: (
            <span className={styles.expandCell}>
              <button
                type='button'
                className={styles.expandButton}
                data-testid={`resolutions-expand-${index}`}
                aria-expanded={expanded}
                aria-label={expanded ? 'Satırı daralt' : 'Satırı genişlet'}
                onClick={() => toggleRow(index)}
              >
                {expanded ? '▾' : '▸'}
              </button>
              {formatLocalDateTime(row.ClosedAtUtc)}
            </span>
          ),
          dealer: (
            <span>
              {row.DealerName ?? DASH}
              <small className={styles.dealerCode}> {row.DealerSapCode}</small>
              {row.IsRemote ? <RemoteBadge testId={`resolutions-row-${index}-remote`} /> : null}
            </span>
          ),
          reason: adminFeedbackReasonLabel(row.ReasonCode),
          comment: (
            <span className={clampClass} title={row.Comment ?? undefined}>
              {row.Comment ?? DASH}
            </span>
          ),
          question: (
            <QuestionAnswerCell
              question={row.Question}
              answer={row.Answer}
              questionSource={row.QuestionSource}
              expanded={expanded}
              testIdPrefix={`resolutions-row-${index}`}
            />
          ),
          status: (
            <span className={styles.statusCell} data-testid={`resolutions-row-${index}-status`}>
              <span
                className={`${styles.statusBadge} ${
                  row.Status === 'improved' ? styles.statusImproved : styles.statusClosed
                }`}
              >
                {resolutionStatusLabel(row.Status)}
              </span>
              {row.ResolutionCount > 1 ? (
                <small className={styles.resolutionCount} data-testid={`resolutions-row-${index}-count`}>
                  {formatCount(row.ResolutionCount)}. kapatma
                </small>
              ) : null}
            </span>
          ),
          note: (
            <span className={clampClass} title={row.InternalNote ?? undefined}>
              {row.InternalNote ?? DASH}
            </span>
          ),
          popup: row.PopupTitle ? (
            <span className={styles.popupCell}>
              <span className={clampClass} title={row.PopupTitle}>
                {row.PopupTitle}
              </span>
              {row.PopupHtml ? (
                <button
                  type='button'
                  className={styles.linkButton}
                  data-testid={`resolutions-preview-${index}`}
                  onClick={() => setPreviewRow(row)}
                >
                  Görüntüle
                </button>
              ) : null}
            </span>
          ) : (
            DASH
          ),
          announced: (
            <span data-testid={`resolutions-row-${index}-announced`}>{row.Announced ? 'Evet' : 'Hayır'}</span>
          ),
          closedBy: row.ClosedBy ?? DASH,
          views: row.Announced ? (
            <span data-testid={`resolutions-row-${index}-views`}>
              {formatCount(row.ViewCount)} kullanıcı
              {row.FirstSeenAtUtc ? (
                <small className={styles.dealerCode}> · ilk: {formatLocalDateTime(row.FirstSeenAtUtc)}</small>
              ) : null}
            </span>
          ) : (
            DASH
          ),
          action: (
            <button
              type='button'
              className={`${styles.secondaryButton} ${styles.rowActionButton}`}
              data-testid={`resolutions-reopen-${index}`}
              disabled={reopeningId !== null}
              onClick={() => reopen(row)}
            >
              Yeniden aç
            </button>
          ),
        }
      }),
    // toggleRow/reopen her render'da yenidir ama yalnız state'e dokunur; bağımlılığa girmesi gerekmez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [report, expandedRows, reopeningId]
  )

  return (
    <section className={styles.page} data-testid='resolutions-tab-panel'>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Site Raporları</span>
          <h1>Tamamlananlar</h1>
          <p>
            Sonuçlandırılmış olumsuz geri bildirimler. Tarih filtresi kapatma zamanına uygulanır; yeniden açılan kayıt
            Geri Bildirimler listesine döner.
          </p>
        </div>
        <span className={styles.privacyBadge}>Bayi düzeyinde gösterilir</span>
      </header>

      <div className={styles.usageToolbar}>
        <div className={styles.toolbarSelect}>
          <IPSelectBox
            id='resolutions-status-filter'
            aria-label='Statü filtresi'
            options={statusOptions}
            value={statusOptions.find(option => option.value === (status ?? '')) ?? statusOptions[0]}
            onChangeSingle={option => {
              setStatus(option?.value ? (String(option.value) as FeedbackResolutionStatus) : undefined)
              setPage(1)
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className={styles.toolbarSelect}>
          <IPSelectBox
            id='resolutions-announced-filter'
            aria-label='Duyuru filtresi'
            options={announcedOptions}
            value={announcedOptions.find(option => option.value === announced) ?? announcedOptions[0]}
            onChangeSingle={option => {
              setAnnounced((option?.value ? String(option.value) : '') as AnnouncedFilter)
              setPage(1)
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <input
          type='text'
          data-testid='resolutions-search'
          className={styles.dateInput}
          value={searchDraft}
          aria-label='Bayi araması'
          placeholder='Bayi ünvanı veya SAP kodu'
          onChange={event => setSearchDraft(event.target.value)}
        />
        <button
          type='button'
          data-testid='resolutions-export'
          className={styles.secondaryButton}
          disabled={exporting}
          onClick={exportToExcel}
        >
          Excel&apos;e Aktar
        </button>
      </div>

      {loadState === 'error' ? (
        <section className={`${styles.statePanel} ${styles.errorState}`} role='alert' data-testid='resolutions-error'>
          <span className={styles.stateIcon} aria-hidden='true'>
            !
          </span>
          <h2>Tamamlananlar yüklenemedi</h2>
          <p>{errorMessage}</p>
        </section>
      ) : null}

      {loadState === 'empty' ? (
        <section className={styles.statePanel} role='status' data-testid='resolutions-empty'>
          <span className={styles.stateIcon} aria-hidden='true'>
            —
          </span>
          <h2>Henüz veri yok</h2>
          <p>Seçilen aralıkta ve filtrelerde sonuçlandırılmış geri bildirim bulunamadı.</p>
        </section>
      ) : null}

      {loadState === 'loading' ? (
        <section className={styles.statePanel} role='status' aria-live='polite' data-testid='resolutions-loading'>
          <span className={styles.loadingMark} aria-hidden='true' />
          <h2>Tamamlananlar yükleniyor</h2>
        </section>
      ) : null}

      {report && loadState === 'ready' ? (
        <>
          <div className={styles.reportTable}>
            <Table columns={columns} data={rows} initialPageSize={PAGE_SIZE} hidePagination />
          </div>
          <UsagePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalRows={report.Page.TotalRows}
            onPageChange={setPage}
            testIdPrefix='resolutions'
          />
        </>
      ) : null}

      {/* Bayinin gördüğü popup'ın aynısı (başlık + içerik). */}
      {previewRow && previewRow.PopupTitle && previewRow.PopupHtml ? (
        <FeedbackReplyPopup
          title={previewRow.PopupTitle}
          html={previewRow.PopupHtml}
          onClose={() => setPreviewRow(null)}
        />
      ) : null}
    </section>
  )
}
